import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useGetQuestListForAssigning } from "../../../../hooks/admin/quest/useGetQuestListForAssiging";
import Skeleton from "react-loading-skeleton";
import "./QuestSelector.css";

/**
 * @typedef {Object} QuestSelectorProps
 * @property {(selectedIds: number[]) => void} onSelectionChange - Callback fired when selection changes
 * @property {boolean} [disabled] - If true, disables all interactions
 * @property {Set<number>} [preSelectedQuestIds] - Set of quest IDs already assigned to the school
 * @property {boolean} [externalLoading] - If true, shows loading skeleton (e.g. while fetching school quest list)
 * @property {() => void} [onRetry] - Callback fired when retry is clicked, to re-fetch external data
 */

/**
 * A reusable quest selector with search, select-all/deselect-all,
 * a scrollable checkbox list, and a counter badge ("10 of 20 selected").
 *
 * @param {QuestSelectorProps} props
 */
const QuestSelector = ({
  onSelectionChange,
  disabled = false,
  preSelectedQuestIds,
  externalLoading = false,
  onRetry,
}) => {
  const { quests, loading, error, getQuestListForAssigning } =
    useGetQuestListForAssigning();

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const lastProcessedQuestsRef = useRef(null);

  // Fetch quests on mount
  useEffect(() => {
    getQuestListForAssigning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-select quests already assigned to the school.
  // Uses a ref to detect when quests change (e.g. after retry) so we re-apply pre-selection.
  useEffect(() => {
    if (!quests || quests.length === 0 || externalLoading) return;
    if (quests === lastProcessedQuestsRef.current) return; // already processed this list

    lastProcessedQuestsRef.current = quests;

    if (preSelectedQuestIds && preSelectedQuestIds.size > 0) {
      const preSelected = new Set(
        quests.filter((q) => preSelectedQuestIds.has(q.id)).map((q) => q.id),
      );
      setSelectedIds(preSelected);
      onSelectionChange?.(Array.from(preSelected));
    } else {
      setSelectedIds(new Set());
      onSelectionChange?.([]);
    }
  }, [quests, externalLoading, preSelectedQuestIds, onSelectionChange]);

  // Filtered list based on search query
  const filteredQuests = useMemo(() => {
    if (!quests) return [];
    if (!searchQuery.trim()) return quests;

    const q = searchQuery.toLowerCase();
    return quests.filter((quest) => quest.title.toLowerCase().includes(q));
  }, [quests, searchQuery]);

  // Toggle a single quest
  const handleToggle = useCallback(
    (id) => {
      if (disabled) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        onSelectionChange?.(Array.from(next));
        return next;
      });
    },
    [disabled, onSelectionChange],
  );

  // Select all / Deselect all (operates on ALL quests, not just filtered)
  const handleToggleAll = useCallback(() => {
    if (disabled || !quests) return;

    if (selectedIds.size === quests.length) {
      // Deselect all
      setSelectedIds(new Set());
      onSelectionChange?.([]);
    } else {
      // Select all
      const allIds = new Set(quests.map((q) => q.id));
      setSelectedIds(allIds);
      onSelectionChange?.(Array.from(allIds));
    }
  }, [disabled, quests, selectedIds.size, onSelectionChange]);

  const isLoading = loading || externalLoading;
  const totalCount = quests?.length || 0;
  const selectedCount = selectedIds.size;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  return (
    <div className={`quest-selector ${disabled ? "opacity-50" : ""}`}>
      {/* Header */}
      <div className="quest-selector__header">
        <h5 className="quest-selector__title">
          Assign Quests
          {totalCount > 0 && (
            <span className="quest-selector__badge">
              {selectedCount} of {totalCount} selected
            </span>
          )}
        </h5>

        {totalCount > 0 && (
          <button
            type="button"
            className="quest-selector__toggle-btn"
            onClick={handleToggleAll}
            disabled={disabled}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        )}
      </div>

      {/* Search */}
      {!isLoading && totalCount > 0 && (
        <div className="quest-selector__search-wrapper">
          <i className="bi bi-search quest-selector__search-icon" />
          <input
            type="text"
            className="quest-selector__search"
            placeholder="Search quests…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="quest-selector__loading">
          <Skeleton count={5} height={40} style={{ marginBottom: 8 }} />
        </div>
      )}

      {/* Error state */}
      {!isLoading && error && (
        <div className="quest-selector__error">
          <div>
            <i className="bi bi-exclamation-triangle" /> {error}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger mt-2"
            onClick={() => {
              lastProcessedQuestsRef.current = null;
              getQuestListForAssigning();
              onRetry?.();
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Quest list */}
      {!isLoading && !error && totalCount > 0 && (
        <div className="quest-selector__list">
          {filteredQuests.length === 0 ? (
            <div className="quest-selector__empty">
              <i className="bi bi-search" />
              No quests match "{searchQuery}"
            </div>
          ) : (
            filteredQuests.map((quest) => {
              const isSelected = selectedIds.has(quest.id);
              return (
                <div
                  key={quest.id}
                  className={`quest-selector__item ${isSelected ? "quest-selector__item--selected" : ""}`}
                  onClick={() => handleToggle(quest.id)}
                  role="checkbox"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      handleToggle(quest.id);
                    }
                  }}
                >
                  <div className="quest-selector__checkbox">
                    <i className="bi bi-check quest-selector__check-icon" />
                  </div>

                  <span className="quest-selector__item-title">
                    {quest.title}
                  </span>

                  {quest.type && (
                    <span className="quest-selector__item-type">
                      {quest.type}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Empty (no quests at all) */}
      {!isLoading && !error && totalCount === 0 && (
        <div className="quest-selector__empty">
          <i className="bi bi-inbox" />
          No quests available
        </div>
      )}
    </div>
  );
};

export default QuestSelector;
